import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Row, Col, Button } from 'reactstrap';
import { CHASSIS_VIN_REGEX, validateLicensePlate, validatePositiveInteger } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import { LICENSE_PLATE_STATUSES } from '../../../../_helper/schemaAllowedValues';

const LicensePlateDataModal = ({ isOpen, toggle, mode, selectedLicensePlate, refreshData, customerId, insuranceCompanies }) => {
    const initialFormState = {
        licensePlateNumber: '',
        licensePlateStatus: '',
        carchassisNumber: '',
        isThereLovClaim: false,
        hasCarSoldBefore: false,
        carBrand: '',
        carModel: '',
        carYear: '',
        carOwner: '',
        description: '',
        trafficInsurancePolicy: {
            trafficInsuranceProvider: null,
            trafficInsuranceNumber: '',
            trafficInsuranceStart: '',
            trafficInsuranceExpiry: ''
        },
        cascoInsurancePolicy: {
            cascoInsuranceProvider: null,
            cascoInsuranceNumber: '',
            cascoInsuranceStart: '',
            cascoInsuranceExpiry: ''
        },
        customer: customerId
    };

    const validationSchema = useMemo(() => ({
        licensePlateNumber: {
            required: true,
            message: 'Plaka numarası zorunludur',
            validator: validateLicensePlate,
            validatorMessage: 'Geçerli bir plaka giriniz (örn: 34 ABC 123).',
        },
        carchassisNumber: {
            regex: CHASSIS_VIN_REGEX,
            regexMessage: 'Geçerli bir 17 haneli şasi numarası girin (I, O, Q hariç).',
        },
        carYear: {
            custom: (value) => {
                if (!value) return null;
                if (!validatePositiveInteger(value) || String(value).length !== 4) {
                    return 'Yıl 4 haneli bir sayı olmalıdır.';
                }
                if (parseInt(value) < 1900 || parseInt(value) > new Date().getFullYear() + 1) {
                    return 'Geçerli bir yıl girin.';
                }
                return null;
            },
        },
    }), []);

    const customHandlers = useMemo(() => ({
        trafficInsuranceNumber: (value, prev) => ({
            ...prev,
            trafficInsurancePolicy: {
                ...prev.trafficInsurancePolicy,
                trafficInsuranceNumber: value,
            },
        }),
        cascoInsuranceNumber: (value, prev) => ({
            ...prev,
            cascoInsurancePolicy: {
                ...prev.cascoInsurancePolicy,
                cascoInsuranceNumber: value,
            },
        }),
        isThereLovClaim: (value, prev) => ({ ...prev, isThereLovClaim: value === 'true' }),
        hasCarSoldBefore: (value, prev) => ({ ...prev, hasCarSoldBefore: value === 'true' }),
    }), []);

    const licensePlateApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/licenseplate';

    const {
        formData,
        setFormData,
        errors,
        setErrors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedLicensePlate,
        initialData: initialFormState,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const trafficProvider = selectedItem?.trafficInsurancePolicy?.trafficInsuranceProvider || null;
            const cascoProvider = selectedItem?.cascoInsurancePolicy?.cascoInsuranceProvider || null;

            const formattedTrafficStart = selectedItem?.trafficInsurancePolicy?.trafficInsuranceStart
                ? selectedItem.trafficInsurancePolicy.trafficInsuranceStart.split('T')[0]
                : '';
            const formattedTrafficExpiry = selectedItem?.trafficInsurancePolicy?.trafficInsuranceExpiry
                ? selectedItem.trafficInsurancePolicy.trafficInsuranceExpiry.split('T')[0]
                : '';
            const formattedCascoStart = selectedItem?.cascoInsurancePolicy?.cascoInsuranceStart
                ? selectedItem.cascoInsurancePolicy.cascoInsuranceStart.split('T')[0]
                : '';
            const formattedCascoExpiry = selectedItem?.cascoInsurancePolicy?.cascoInsuranceExpiry
                ? selectedItem.cascoInsurancePolicy.cascoInsuranceExpiry.split('T')[0]
                : '';

            return {
                ...initialData,
                ...selectedItem,
                customer: customerId ? customerId : null,
                trafficInsurancePolicy: {
                    ...initialData.trafficInsurancePolicy,
                    ...selectedItem?.trafficInsurancePolicy,
                    trafficInsuranceProvider: trafficProvider,
                    trafficInsuranceStart: formattedTrafficStart,
                    trafficInsuranceExpiry: formattedTrafficExpiry,
                },
                cascoInsurancePolicy: {
                    ...initialData.cascoInsurancePolicy,
                    ...selectedItem?.cascoInsurancePolicy,
                    cascoInsuranceProvider: cascoProvider,
                    cascoInsuranceStart: formattedCascoStart,
                    cascoInsuranceExpiry: formattedCascoExpiry,
                },
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint: licensePlateApiEndpoint,
            refreshData,
            componentName: 'Plaka',
            buildPayload: (data) => {
                const getProviderId = (providerValue) => {
                    if (!providerValue) return null;
                    return typeof providerValue === 'object' ? providerValue.id : providerValue;
                };

                const formatIsoDate = (dateString) => {
                    if (!dateString) return null;
                    try {
                        const parsedDate = new Date(dateString);
                        return isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
                    } catch {
                        return null;
                    }
                };

                const trafficProviderId = getProviderId(data.trafficInsurancePolicy?.trafficInsuranceProvider);
                const trafficInsurancePolicyPayload = trafficProviderId ? {
                    trafficInsuranceProvider: [trafficProviderId],
                    trafficInsuranceNumber: data.trafficInsurancePolicy?.trafficInsuranceNumber || null,
                    trafficInsuranceStart: formatIsoDate(data.trafficInsurancePolicy?.trafficInsuranceStart),
                    trafficInsuranceExpiry: formatIsoDate(data.trafficInsurancePolicy?.trafficInsuranceExpiry)
                } : null;

                const cascoProviderId = getProviderId(data.cascoInsurancePolicy?.cascoInsuranceProvider);
                const cascoInsurancePolicyPayload = cascoProviderId ? {
                    cascoInsuranceProvider: [cascoProviderId],
                    cascoInsuranceNumber: data.cascoInsurancePolicy?.cascoInsuranceNumber || null,
                    cascoInsuranceStart: formatIsoDate(data.cascoInsurancePolicy?.cascoInsuranceStart),
                    cascoInsuranceExpiry: formatIsoDate(data.cascoInsurancePolicy?.cascoInsuranceExpiry)
                } : null;

                return {
                    customer: customerId || null,
                    licensePlateNumber: data.licensePlateNumber || "",
                    licensePlateStatus: data.licensePlateStatus || "",
                    carchassisNumber: data.carchassisNumber || null,
                    isThereLovClaim: Boolean(data.isThereLovClaim),
                    hasCarSoldBefore: Boolean(data.hasCarSoldBefore),
                    carBrand: data.carBrand || null,
                    carModel: data.carModel || null,
                    carYear: data.carYear ? data.carYear.toString() : null,
                    carOwner: data.carOwner || null,
                    description: data.description || null,
                    trafficInsurancePolicy: trafficInsurancePolicyPayload,
                    cascoInsurancePolicy: cascoInsurancePolicyPayload
                };
            },
        },
    });

    const handleTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const provider = providerId ? insuranceCompanies?.find(ic => ic.id === providerId) || providerId : null;

        setFormData(prev => ({
            ...prev,
            trafficInsurancePolicy: {
                ...prev.trafficInsurancePolicy,
                trafficInsuranceProvider: provider
            }
        }));

        if (errors.trafficInsurancePolicy?.trafficInsuranceProvider) {
            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceProvider: undefined }}));
        }
    };

    const handleCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const provider = providerId ? insuranceCompanies?.find(ic => ic.id === providerId) || providerId : null;

        setFormData(prev => ({
            ...prev,
            cascoInsurancePolicy: {
                ...prev.cascoInsurancePolicy,
                cascoInsuranceProvider: provider
            }
        }));

        if (errors.cascoInsurancePolicy?.cascoInsuranceProvider) {
            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceProvider: undefined }}));
        }
    };

    const getSelectedProviderId = (policy) => {
        if (!policy || (!policy.trafficInsuranceProvider && !policy.cascoInsuranceProvider)) return '';
        const provider = policy.trafficInsuranceProvider || policy.cascoInsuranceProvider;
        return typeof provider === 'object' && provider !== null ? provider.id : (provider || '');
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Plaka Ekle' : 'Plaka Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="licensePlateNumber">Plaka Numarası <span className="text-danger">*</span></Label>
                        <Input
                            id="licensePlateNumber"
                            name="licensePlateNumber"
                            value={formData.licensePlateNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Örn: 34 AB 123"
                            invalid={!!errors.licensePlateNumber}
                        />
                        <FormFeedback>{errors.licensePlateNumber}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="licensePlateStatus">Plaka Durumu</Label>
                        <Input
                            type="select"
                            id="licensePlateStatus"
                            name="licensePlateStatus"
                            value={formData.licensePlateStatus || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.licensePlateStatus}
                        >
                            <option value="">Seçiniz</option>
                            {LICENSE_PLATE_STATUSES.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.licensePlateStatus}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="carchassisNumber">Şasi Numarası</Label>
                        <Input
                            id="carchassisNumber"
                            name="carchassisNumber"
                            value={formData.carchassisNumber || ''}
                            onChange={handleInputChange}
                            placeholder="17 Haneli Şasi Numarası"
                            invalid={!!errors.carchassisNumber}
                        />
                        <FormFeedback>{errors.carchassisNumber}</FormFeedback>
                    </FormGroup>

                    <Row>
                        <Col md={6}>
                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label for="isThereLovClaim" className="me-3 mb-0"> Değer Kaybı Talebi?</Label>
                                <div className="d-flex">
                                    <div className="radio radio-success me-3 d-flex align-items-center">
                                        <Input
                                            id="isThereLovClaimTrue"
                                            type="radio"
                                            name="isThereLovClaim"
                                            value="true"
                                            checked={formData.isThereLovClaim === true}
                                            onChange={handleInputChange}
                                        />
                                        <Label htmlFor="isThereLovClaimTrue" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-danger d-flex align-items-center">
                                        <Input
                                            id="isThereLovClaimFalse"
                                            type="radio"
                                            name="isThereLovClaim"
                                            value="false"
                                            checked={formData.isThereLovClaim === false}
                                            onChange={handleInputChange}
                                        />
                                        <Label htmlFor="isThereLovClaimFalse" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label for="hasCarSoldBefore" className="me-3 mb-0">Araç Satıldı Mı?</Label>
                                <div className="d-flex">
                                    <div className="radio radio-success me-3 d-flex align-items-center">
                                        <Input
                                            id="hasCarSoldBeforeTrue"
                                            type="radio"
                                            name="hasCarSoldBefore"
                                            value="true"
                                            checked={formData.hasCarSoldBefore === true}
                                            onChange={handleInputChange}
                                        />
                                        <Label htmlFor="hasCarSoldBeforeTrue" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-danger d-flex align-items-center">
                                        <Input
                                            id="hasCarSoldBeforeFalse"
                                            type="radio"
                                            name="hasCarSoldBefore"
                                            value="false"
                                            checked={formData.hasCarSoldBefore === false}
                                            onChange={handleInputChange}
                                        />
                                        <Label htmlFor="hasCarSoldBeforeFalse" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carBrand">Araç Markası</Label>
                                <Input
                                    id="carBrand"
                                    name="carBrand"
                                    value={formData.carBrand || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: BMW"
                                    invalid={!!errors.carBrand}
                                />
                                <FormFeedback>{errors.carBrand}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carModel">Araç Modeli</Label>
                                <Input
                                    id="carModel"
                                    name="carModel"
                                    value={formData.carModel || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 320i"
                                    invalid={!!errors.carModel}
                                />
                                <FormFeedback>{errors.carModel}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carYear">Araç Yılı</Label>
                                <Input
                                    type="number"
                                    id="carYear"
                                    name="carYear"
                                    value={formData.carYear || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 2020"
                                    invalid={!!errors.carYear}
                                />
                                <FormFeedback>{errors.carYear}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carOwner">Araç Sahibi</Label>
                                <Input
                                    id="carOwner"
                                    name="carOwner"
                                    value={formData.carOwner || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: Ahmet Yılmaz"
                                    invalid={!!errors.carOwner}
                                />
                                <FormFeedback>{errors.carOwner}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <h5>Trafik Sigortası</h5>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceProvider">Sağlayıcı</Label>
                                <Input
                                    type="select"
                                    id="trafficInsuranceProvider"
                                    name="trafficInsuranceProvider"
                                    value={getSelectedProviderId(formData.trafficInsurancePolicy)}
                                    onChange={handleTrafficInsuranceProviderChange}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceProvider}
                                >
                                    <option value="">Seçiniz</option>
                                    {insuranceCompanies?.map(company => (
                                        <option key={`trafik-${company.id}`} value={company.id}>{company.name}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceProvider}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceNumber">Poliçe No</Label>
                                <Input
                                    id="trafficInsuranceNumber"
                                    name="trafficInsuranceNumber"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                    onChange={handleInputChange}
                                    placeholder="Poliçe Numarası"
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceNumber}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceStart">Başlangıç Tarihi</Label>
                                <Input
                                    type="date"
                                    id="trafficInsuranceStart"
                                    name="trafficInsuranceStart"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceStart || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            trafficInsurancePolicy: {
                                                ...prev.trafficInsurancePolicy,
                                                trafficInsuranceStart: value
                                            }
                                        }));

                                        if (errors.trafficInsurancePolicy?.trafficInsuranceStart) {
                                            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceStart: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceStart}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceStart}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceExpiry">Bitiş Tarihi</Label>
                                <Input
                                    type="date"
                                    id="trafficInsuranceExpiry"
                                    name="trafficInsuranceExpiry"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceExpiry || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            trafficInsurancePolicy: {
                                                ...prev.trafficInsurancePolicy,
                                                trafficInsuranceExpiry: value
                                            }
                                        }));

                                        if (errors.trafficInsurancePolicy?.trafficInsuranceExpiry) {
                                            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceExpiry: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceExpiry}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceExpiry}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <h5>Kasko Sigortası</h5>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceProvider">Sağlayıcı</Label>
                                <Input
                                    type="select"
                                    id="cascoInsuranceProvider"
                                    name="cascoInsuranceProvider"
                                    value={getSelectedProviderId(formData.cascoInsurancePolicy)}
                                    onChange={handleCascoInsuranceProviderChange}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceProvider}
                                >
                                    <option value="">Seçiniz</option>
                                    {insuranceCompanies?.map(company => (
                                        <option key={`kasko-${company.id}`} value={company.id}>{company.name}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceProvider}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceNumber">Poliçe No</Label>
                                <Input
                                    id="cascoInsuranceNumber"
                                    name="cascoInsuranceNumber"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                    onChange={handleInputChange}
                                    placeholder="Poliçe Numarası"
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceNumber}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceStart">Başlangıç Tarihi</Label>
                                <Input
                                    type="date"
                                    id="cascoInsuranceStart"
                                    name="cascoInsuranceStart"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceStart || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            cascoInsurancePolicy: {
                                                ...prev.cascoInsurancePolicy,
                                                cascoInsuranceStart: value
                                            }
                                        }));

                                        if (errors.cascoInsurancePolicy?.cascoInsuranceStart) {
                                            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceStart: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceStart}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceStart}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceExpiry">Bitiş Tarihi</Label>
                                <Input
                                    type="date"
                                    id="cascoInsuranceExpiry"
                                    name="cascoInsuranceExpiry"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceExpiry || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            cascoInsurancePolicy: {
                                                ...prev.cascoInsurancePolicy,
                                                cascoInsuranceExpiry: value
                                            }
                                        }));

                                        if (errors.cascoInsurancePolicy?.cascoInsuranceExpiry) {
                                            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceExpiry: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceExpiry}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceExpiry}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={formData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Açıklama giriniz..."
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="success" outline onClick={handleSubmit} disabled={isSubmitting} className="rounded-pill">
                    {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                </Button>
                <Button color="danger" outline onClick={handleCancel} disabled={isSubmitting} className="ms-2 rounded-pill">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LicensePlateDataModal;
